import {
  Avatar,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  type Theme,
  Tooltip,
  type TooltipProps,
  Typography,
  tooltipClasses,
  useMediaQuery
} from '@mui/material';
import type { FC } from 'react';
import type { IIsPdf, ResultsByTapType } from '../../../models';
import { defineColorFromTrendRating, defineCols, defineRatingColor, readableDate } from '../../../utils';
import { TrendIcon } from '../../Functions';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} enterDelay={500} enterNextDelay={500} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.avani.lightTurc,
    maxWidth: 320,
    padding: 0,
    borderRadius: '16px',
    border: `1px solid ${theme.colors.avani.magenta}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch'
  }
}));

const TooltipContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: '10px',
  width: '100%',
  fontSize: 'inherit',
  color: theme.colors.avani.darkTitleText
}));

interface ResultByTableProps extends IIsPdf {
  results: ResultsByTapType[];
  theme: Theme;
  previousFirstDate?: string;
  previousSecondDate?: string;
  previousThirdDate?: string;
}

export const ResultsByTapTable: FC<ResultByTableProps> = ({
  theme,
  results,
  previousFirstDate,
  previousSecondDate,
  previousThirdDate,
  isPDF
}) => {
  const hasAnyChanged = results.some(({ changedProduct }) => changedProduct);
  const COL_WIDTHS = defineCols('resultsByTap', previousFirstDate, previousSecondDate, previousThirdDate);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Table sx={{ marginTop: 2, tableLayout: 'fixed' }} stickyHeader>
      {!isMobile && (
        <colgroup>
          {COL_WIDTHS.map((w, idx) => (
            <col key={idx} style={{ width: w }} />
          ))}
        </colgroup>
      )}
      <TableHead
        sx={{
          '& td, & th': { whiteSpace: 'nowrap' },
          '& th': {
            backgroundColor: theme.colors.avani.lightTurc,
            borderBottom: `2px solid ${theme.colors.avani.teal}`
          }
        }}
      >
        <TableRow>
          <TableCell>
            <Typography variant={'h4'}>Bar</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant={'h4'}>Tap</Typography>
          </TableCell>
          <TableCell>
            <Typography variant={'h4'}>Product</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant={'h4'}>Now</Typography>
          </TableCell>
          {!isMobile && (
            <>
              {previousFirstDate && (
                <>
                  <TableCell align="center"></TableCell>
                  <TableCell align="center">
                    <Typography variant={'h4'}>{readableDate(previousFirstDate)}</Typography>
                  </TableCell>
                  {previousSecondDate && (
                    <>
                      <TableCell align="center"></TableCell>
                      <TableCell align="center">
                        <Typography variant={'h4'}>{readableDate(previousSecondDate)}</Typography>
                      </TableCell>
                      {previousThirdDate && (
                        <>
                          <TableCell align="center"></TableCell>
                          <TableCell align="center">
                            <Typography variant={'h4'}>{readableDate(previousThirdDate)}</Typography>
                          </TableCell>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </TableRow>
      </TableHead>
      <TableBody sx={{ '& td, & th': { whiteSpace: 'normal', wordBreak: 'break-word' } }}>
        {results.map(
          (
            {
              productName,
              barName,
              tapPosition,
              currentRating,
              changedProduct,
              previousFirst,
              previousSecond,
              previousThird
            },
            i
          ) => {
            return (
              <TableRow
                key={i}
                sx={{
                  '& td': {
                    color: `${defineColorFromTrendRating(
                      'preClean',
                      previousFirst?.outOfTrendRating ?? null,
                      theme
                    )} !important`
                  }
                }}
              >
                <TableCell>{barName}</TableCell>
                <TableCell align="center">{tapPosition}</TableCell>
                {changedProduct ? (
                  <HtmlTooltip
                    flow-cursor
                    placement="bottom-start"
                    title={
                      <TooltipContent>
                        <div style={{ display: 'flex' }}>
                          <span style={{ color: theme.colors.avani.magenta }}>*</span>
                          <Typography variant="body2">Product changed</Typography>
                        </div>
                      </TooltipContent>
                    }
                  >
                    <TableCell>
                      {productName ?? '-'}
                      <span style={{ color: theme.colors.avani.magenta }}>*</span>
                    </TableCell>
                  </HtmlTooltip>
                ) : (
                  <TableCell>{productName ?? '-'}</TableCell>
                )}
                <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Avatar
                      sx={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: defineRatingColor(currentRating, theme),
                        color: theme.colors.avani.darkTitleText,
                        border: `1px solid ${theme.colors.avani.teal}`,
                        fontSize: 14,
                        padding: '3px 0 0 0',
                        textAlign: 'center'
                      }}
                    >
                      {currentRating ?? '-'}
                    </Avatar>
                  </div>
                </TableCell>
                {!isMobile && (
                  <>
                    {previousFirstDate && (
                      <>
                        <TableCell align="center">
                          <TrendIcon type="preClean" rating={previousFirst?.outOfTrendRating ?? null} theme={theme} />
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Avatar
                              sx={{
                                width: '30px',
                                height: '30px',
                                backgroundColor: defineRatingColor(previousFirst?.preCleanRating ?? null, theme),
                                color: theme.colors.avani.darkTitleText,
                                border: `1px solid ${theme.colors.avani.teal}`,
                                fontSize: 14,
                                padding: '3px 0 0 0',
                                textAlign: 'center'
                              }}
                            >
                              {previousFirst?.preCleanRating ?? '-'}
                            </Avatar>
                          </div>
                        </TableCell>
                      </>
                    )}
                    {previousSecondDate && (
                      <>
                        <TableCell align="center">
                          <TrendIcon type="preClean" rating={previousSecond?.outOfTrendRating ?? null} theme={theme} />
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Avatar
                              sx={{
                                width: '30px',
                                height: '30px',
                                backgroundColor: defineRatingColor(previousSecond?.preCleanRating ?? null, theme),
                                color: theme.colors.avani.darkTitleText,
                                border: `1px solid ${theme.colors.avani.teal}`,
                                fontSize: 14,
                                padding: '3px 0 0 0',
                                textAlign: 'center'
                              }}
                            >
                              {previousSecond?.preCleanRating ?? '-'}
                            </Avatar>
                          </div>
                        </TableCell>
                      </>
                    )}
                    {previousThirdDate && (
                      <>
                        <TableCell align="center">
                          <TrendIcon type="preClean" rating={previousThird?.outOfTrendRating ?? null} theme={theme} />
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Avatar
                              sx={{
                                width: '30px',
                                height: '30px',
                                backgroundColor: defineRatingColor(previousThird?.preCleanRating ?? null, theme),
                                color: theme.colors.avani.darkTitleText,
                                border: `1px solid ${theme.colors.avani.teal}`,
                                fontSize: 14,
                                padding: '3px 0 0 0',
                                textAlign: 'center'
                              }}
                            >
                              {previousThird?.preCleanRating ?? '-'}
                            </Avatar>
                          </div>
                        </TableCell>
                      </>
                    )}
                  </>
                )}
              </TableRow>
            );
          }
        )}
        {isPDF && hasAnyChanged && (
          <TableCell>
            <span style={{ color: theme.colors.avani.magenta }}>*</span>
            <Typography variant="body2">Product changed</Typography>
          </TableCell>
        )}
      </TableBody>
    </Table>
  );
};
